'use client';

import { getClasses } from '@/_utils/classnames';
import { type FC, useContext } from 'react';
import { Alert, Heading, Flex } from '@/_components';
import styles from './LayoutContent.module.css';
import type { LayoutContentProps } from './LayoutContent.types';
import { UserContext } from '@/_contexts/UserProvider';
import { LayoutContext } from '@/_contexts/LayoutProvider';

export const LayoutContent: FC<LayoutContentProps> = ({
  alert,
  children,
  headerLeft = null,
  headerRight = null,
  title,
}) => {
  const { isAssistantOpen } = useContext(LayoutContext);
  const { isUserReadOnly, isCompanyReadOnly } = useContext(UserContext);

  const contentClasses: string = getClasses(
    styles.layoutContent,
    'flex flex-column gap-12',
    {
      [styles.layoutContent__withAssistant]: isAssistantOpen,
    },
  );

  return (
    <section className={contentClasses} suppressHydrationWarning={true}>
      <Flex isColumn={true} gap={10}>
        {!!alert && alert}
        {(isUserReadOnly || isCompanyReadOnly) && (
          <Alert type="info" icon="info">
            {isUserReadOnly
              ? 'This account is read-only. If you need to upload documents or answer questionnaires, please send us an email.'
              : 'This entity is read-only. If you need to upload documents or answer questionnaires, please send us an email.'}
          </Alert>
        )}
        {title && (
          <Flex isYCentered={true} isBetween={true} isWrapping={true} gap={6}>
            <Flex isYCentered={true} gap={8}>
              {headerLeft}
              <Heading
                level={1}
                data-testid={
                  title === 'Collect'
                    ? 'LayoutContent-collect-heading'
                    : title === 'Entities'
                      ? 'LayoutContent-entities-heading'
                      : title === 'Users'
                        ? 'LayoutContent-users-heading'
                        : title === 'Deliver'
                          ? 'LayoutContent-deliver-heading'
                          : title === 'Connect'
                            ? 'LayoutContent-connect-heading'
                            : undefined
                }
              >
                {title}
              </Heading>
            </Flex>
            {headerRight}
          </Flex>
        )}
        {children}
      </Flex>
    </section>
  );
};
